import { ErrorScreen } from '../../../../Error';
import Button from '@/components/Button';
import { LoadingContainer } from '../../../shared/components/loading-container';
import { Suspense, useMemo, useState } from 'react';
import { Hero } from '../hero';
import { useAssessmentPrePost } from '../../hooks/useAssessmentPrePost';
import { useAssessmentStore } from '../../store/useAssessmentStore';
import { ConsoleCJ } from '../../../../Error/Console';
import { AssessmentStatus } from '../../../shared/store/useChapterStore';
import Container from '@/components/Container';
import PageHeader from '@/components/PageHeader';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/services/lib/utils';
import { InformationCircleIcon } from '@heroicons/react/20/solid';
import EdwinDialog from '@/components/Dialog';

const AssessmentPrePostScreenComponent = () => {
  const {
    title,
    description,
    cohortData,
    errorCohort,
    loadingSubmit,
    mission,
    onUpdateCohort,
    onDeleteCohort,
    onSubmit,
    loadingCreateAssessment,
    status,
  } = useAssessmentPrePost();
  const assessmentPage = useAssessmentStore((state) => state.assessmentPage);
  const [answer, setAnswer] = useState(0);
  const [notes, setNotes] = useState('');
  const [openScaleDialog, setOpenScaleDialog] = useState(false);
  const max = 10;
  const ticks = [...Array(max + 1)].map((_, i) => i);
  const skipInterval = 1;
  const assessmentSectionTitle =
    status === AssessmentStatus.PRE_ASSESSMENT
      ? assessmentPage?.cohortSection?.preAssess
      : assessmentPage?.cohortSection?.postAssess;
  const scaleDialogTitle = assessmentPage?.cohortSection?.assessmentScaleDialogTitle ?? assessmentSectionTitle;
  const scaleItems = useMemo(
    () =>
      [...(assessmentPage?.assessmentScale ?? [])]
        .filter((item): item is NonNullable<typeof item> => Boolean(item))
        .sort((a, b) => (a.score ?? 0) - (b.score ?? 0)),
    [assessmentPage?.assessmentScale],
  );

  if (errorCohort) {
    ConsoleCJ.customError('AssessmentPrePostScreen', {
      ...errorCohort,
    });
    return <ErrorScreen />;
  }

  return (
    <Container className="!mt-sh-60">
      <div className="col-span-12 lg:col-start-3 lg:col-end-11">
        <PageHeader title={title || 'Assessment'} type="none" />
        <div className="mt-5 pb-10">
          <Hero title={title || ''} description={description || ''} />
          <h2 className="mt-4 font-cadiz text-sh-xs font-bold">Selected Cohort: {cohortData?.cohort?.name || ''}</h2>

          <div className="my-2 mt-6 flex gap-2">
            <h3 className="font-cadiz text-sh-28-32 font-bold">{assessmentSectionTitle}</h3>
            <InformationCircleIcon
              className="mt-1 h-6 w-6 shrink-0 cursor-pointer text-gray-400"
              onClick={() => setOpenScaleDialog(true)}
            />
          </div>
          <p className="font-cadiz text-base text-gray-600">{assessmentPage?.cohortSection?.caption}</p>
          <p className="mt-2 font-cadiz text-base font-bold">{mission?.trait?.trait}</p>

          <div className="mt-5">
            <Slider defaultValue={[answer]} max={max} onValueChange={(value) => setAnswer(value[0])} />
            <span
              className="mt-3 flex w-full items-center justify-between gap-1 px-2.5 text-xs font-medium text-muted-foreground"
              aria-hidden="true">
              {ticks.map((_, i) => (
                <span key={i} className="flex w-0 flex-col items-center justify-center gap-2">
                  <span className={cn('h-1 w-px bg-muted-foreground/70', i % skipInterval !== 0 && 'h-0.5')} />
                  <span className={cn(i % skipInterval !== 0 && 'opacity-0')}>{i}</span>
                </span>
              ))}
            </span>
          </div>

          <div className="mt-5">
            <label className="mb-2 block font-cadiz text-base font-bold text-gray-700">
              {assessmentPage?.cohortSection?.inputs?.notes}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none"
              rows={4}
              placeholder="Enter your notes..."
            />
          </div>
          <div className="mt-4 flex items-start justify-start gap-4">
            <Button
              theme="solid"
              onClick={() => onSubmit({ answer, notes })}
              disabled={loadingSubmit || loadingCreateAssessment}>
              {assessmentPage?.cohortSection?.submitButton}
            </Button>
            <Button theme="outline" onClick={onUpdateCohort} disabled={loadingSubmit}>
              {assessmentPage?.cohortSection?.changeCohortButton}
            </Button>
            <Button theme="outline" onClick={onDeleteCohort} disabled={loadingSubmit}>
              {assessmentPage?.cohortSection?.deleteCohortButton}
            </Button>
          </div>
        </div>
      </div>
      <EdwinDialog
        open={openScaleDialog}
        setOpen={setOpenScaleDialog}
        title={scaleDialogTitle}
        body={
          <div className="space-y-4 text-left">
            {scaleItems.map((item) => (
              <div key={item.score} className="border-b border-gray-100 pb-3 last:border-0">
                <p className="font-cadiz text-sh-xs font-bold">
                  {item.score} - {item.title}
                </p>
                <p className="font-cadiz text-sh-xs text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        }
      />
    </Container>
  );
};

export const AssessmentPrePostScreen = () => {
  return (
    <Suspense fallback={<LoadingContainer title="Assessment" />}>
      <AssessmentPrePostScreenComponent />
    </Suspense>
  );
};
